import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminAnalytics() {
  const [mrrData, setMrrData] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [featureData, setFeatureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(6);
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [months, days]);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const [mrrRes, conversionRes, churnRes, featureRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/mrr?months=${months}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/analytics/conversion-funnel?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/analytics/churn`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/analytics/feature-adoption`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setMrrData(mrrRes.data);
      setConversionData(conversionRes.data);
      setChurnData(churnRes.data);
      setFeatureData(featureRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (reportType) => {
    const token = localStorage.getItem("admin_token");
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/analytics/export/${reportType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${reportType} report exported successfully`);
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const maxMrr = Math.max(...(mrrData?.monthly_data || []).map(m => m.mrr), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Analytics & Reports</h1>
          <p className="text-muted-foreground mt-1">Business metrics and data exports</p>
        </div>
      </div>

      {/* MRR/ARR Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Current MRR</p>
            <span className="text-2xl">💰</span>
          </div>
          <p className="text-4xl font-bold text-foreground">${mrrData?.current_mrr?.toLocaleString() || 0}</p>
          <p className="text-muted-foreground text-sm mt-1">Monthly Recurring Revenue</p>
        </div>
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">ARR</p>
            <span className="text-2xl">📈</span>
          </div>
          <p className="text-4xl font-bold text-foreground">${mrrData?.arr?.toLocaleString() || 0}</p>
          <p className="text-muted-foreground text-sm mt-1">Annual Run Rate</p>
        </div>
      </div>

      {/* MRR Trend Chart */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">MRR Trend</h2>
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value))}
            className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="3">Last 3 months</option>
            <option value="6">Last 6 months</option>
            <option value="12">Last 12 months</option>
          </select>
        </div>
        <div className="flex items-end justify-between gap-2 h-64">
          {mrrData?.monthly_data?.map((month, idx) => {
            const height = (month.mrr / maxMrr) * 100;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center justify-end group">
                <div className="relative w-full">
                  <div
                    className="w-full bg-gradient-to-t from-green-500 to-emerald-400 rounded-t transition-all hover:from-green-400 hover:to-emerald-300"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  />
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 hidden group-hover:block bg-muted px-3 py-2 rounded text-xs text-foreground whitespace-nowrap">
                    <p className="font-bold">${month.mrr}</p>
                    <p className="text-muted-foreground">{month.active_subs} subs</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{month.month_name.split(' ')[0]}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Conversion Funnel & Churn */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">Conversion Funnel</h2>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-3 py-1 bg-muted border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
            </select>
          </div>
          <div className="space-y-4">
            {conversionData?.funnel_stages?.map((stage, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-foreground font-medium">{stage.stage}</span>
                  <span className="text-muted-foreground text-sm">{stage.count} users ({stage.percentage}%)</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-teal-500 to-teal-400 h-3 rounded-full transition-all"
                    style={{ width: `${stage.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 bg-primary/10 border border-teal-500/20 rounded-lg">
            <p className="text-teal-400 font-bold text-lg">{conversionData?.conversion_rate || 0}% Conversion Rate</p>
            <p className="text-muted-foreground text-sm">Trial to paid conversion</p>
          </div>
        </div>

        {/* Churn & Retention */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Churn & Retention</h2>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-muted-foreground text-sm mb-2">Active Paying</p>
                <p className="text-3xl font-bold text-green-400">{churnData?.active_paying || 0}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-muted-foreground text-sm mb-2">Cancelled</p>
                <p className="text-3xl font-bold text-red-400">{churnData?.cancelled || 0}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-foreground font-medium">Retention Rate</span>
                  <span className="text-green-400 font-bold">{churnData?.retention_rate || 0}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-green-500 to-emerald-400 h-3 rounded-full"
                    style={{ width: `${churnData?.retention_rate || 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-foreground font-medium">Churn Rate</span>
                  <span className="text-red-400 font-bold">{churnData?.churn_rate || 0}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-red-500 to-orange-400 h-3 rounded-full"
                    style={{ width: `${churnData?.churn_rate || 0}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="bg-orange-500/10 border border-orange-500/20 p-3 rounded-lg">
                <p className="text-orange-400 text-xs">Past Due</p>
                <p className="text-foreground font-bold text-xl">{churnData?.past_due || 0}</p>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-muted-foreground text-xs">Total Users</p>
                <p className="text-foreground font-bold text-xl">{churnData?.total_users || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Adoption */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Feature Adoption</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featureData?.features?.map((feature, idx) => (
            <div key={idx} className="bg-muted/50 p-6 rounded-xl">
              <h3 className="text-foreground font-medium mb-4">{feature.name}</h3>
              <div className="space-y-3">
                <div className="flex items-baseline gap-2">
                  <p className="text-4xl font-bold text-foreground">{feature.adoption_rate}%</p>
                  <p className="text-muted-foreground text-sm">adoption</p>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-teal-500 to-teal-400 h-2 rounded-full"
                    style={{ width: `${feature.adoption_rate}%` }}
                  />
                </div>
                <p className="text-muted-foreground text-sm">{feature.users} of {featureData.total_users} users</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Export Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => handleExport('users')}
            disabled={exporting}
            className="flex items-center justify-between p-4 bg-muted hover:bg-muted disabled:opacity-50 rounded-lg transition"
          >
            <div>
              <p className="text-foreground font-medium">Users Report</p>
              <p className="text-muted-foreground text-xs">All user data</p>
            </div>
            <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>

          <button
            onClick={() => handleExport('subscriptions')}
            disabled={exporting}
            className="flex items-center justify-between p-4 bg-muted hover:bg-muted disabled:opacity-50 rounded-lg transition"
          >
            <div>
              <p className="text-foreground font-medium">Subscriptions</p>
              <p className="text-muted-foreground text-xs">All subscriptions</p>
            </div>
            <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>

          <button
            onClick={() => handleExport('credits')}
            disabled={exporting}
            className="flex items-center justify-between p-4 bg-muted hover:bg-muted disabled:opacity-50 rounded-lg transition"
          >
            <div>
              <p className="text-foreground font-medium">Credit History</p>
              <p className="text-muted-foreground text-xs">All transactions</p>
            </div>
            <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>

          <button
            onClick={() => handleExport('questions')}
            disabled={exporting}
            className="flex items-center justify-between p-4 bg-muted hover:bg-muted disabled:opacity-50 rounded-lg transition"
          >
            <div>
              <p className="text-foreground font-medium">Questions</p>
              <p className="text-muted-foreground text-xs">Usage metrics</p>
            </div>
            <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
        </div>
        <p className="text-muted-foreground text-xs mt-4">
          💡 All exports use anonymized user IDs for privacy compliance
        </p>
      </div>
    </div>
  );
}
